import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { ensureProtocol, normalizeUrl, getApiBaseUrl } from "@/utils/apiUtils";
import { HttpError, type HttpRequestMeta } from "@/types/http";
import { getToken } from "@/utils/authToken";

function buildInstance(timeoutMs: number = 90000): AxiosInstance {
  return axios.create({
    baseURL: getApiBaseUrl(),
    timeout: timeoutMs,
    withCredentials: false,
    // Add retries for transient failures
    transitional: {
      silentJSONParsing: true,
      forcedJSONParsing: true,
      clarifyTimeoutError: true,
    },
  });
}

async function withAuthHeaders(
  cfg: AxiosRequestConfig = {},
): Promise<AxiosRequestConfig> {
  // Autenticação por token: Authorization: token api_key:api_secret.
  const token = getToken();
  cfg.headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token ? { Authorization: `token ${token}` } : {}),
    ...(cfg.headers || {}),
  } as any;
  return cfg;
}

function toHttpError(err: any, req: HttpRequestMeta): HttpError {
  if (err && err.response) {
    const res = err.response;
    const he = new HttpError(res.data?.message || `HTTP ${res.status}`, {
      status: res.status,
      statusText: res.statusText,
      data: { ...res.data },
      request: req,
      raw: err,
    });
    try {
      window.dispatchEvent(
        new CustomEvent("baseform:error", { detail: { error: he } }),
      );
    } catch {}
    return he;
  }
  const he = new HttpError(err?.message || "Erro de rede", {
    status: null,
    statusText: null,
    data: null,
    request: req,
    raw: err,
  });
  try {
    window.dispatchEvent(
      new CustomEvent("baseform:error", { detail: { error: he } }),
    );
  } catch {}
  return he;
}

export const authHttp = {
  async get<T = any>(
    endpoint: string,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    // Use higher timeout for consolidated data endpoints
    const timeoutMs = endpoint.includes("consolidated") ? 120000 : 90000;
    const client = buildInstance(timeoutMs);
    const cfg = await withAuthHeaders(config);
    const reqMeta: HttpRequestMeta = {
      method: "GET",
      url: client.defaults.baseURL
        ? normalizeUrl(client.defaults.baseURL, endpoint)
        : endpoint,
      headers: cfg.headers,
    };
    try {
      const res = await client.get(endpoint, cfg);
      return res.data as T;
    } catch (e) {
      // Ignore canceled requests - these are expected when aborting
      const isCanceledError =
        e instanceof Error &&
        (e.message === "canceled" ||
          e.name === "CanceledError" ||
          (e as any).code === "ERR_CANCELED");

      if (!isCanceledError) {
        console.error(`[authHttp] GET ${reqMeta.url} ❌ Error:`, e);
      }

      throw toHttpError(e, reqMeta);
    }
  },
  async post<T = any>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const client = buildInstance();
    const cfg = await withAuthHeaders(config);
    const reqMeta: HttpRequestMeta = {
      method: "POST",
      url: client.defaults.baseURL
        ? normalizeUrl(client.defaults.baseURL, endpoint)
        : endpoint,
      headers: cfg.headers,
      body: data,
    };
    try {
      const res = await client.post(endpoint, data, cfg);
      return res.data as T;
    } catch (e) {
      // Ignore canceled requests - these are expected when aborting
      const isCanceledError =
        e instanceof Error &&
        (e.message === "canceled" ||
          e.name === "CanceledError" ||
          (e as any).code === "ERR_CANCELED");

      if (!isCanceledError) {
        console.error(`[authHttp] POST ${reqMeta.url} ❌ Error:`, e);
      }

      throw toHttpError(e, reqMeta);
    }
  },
  async put<T = any>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const client = buildInstance();
    const cfg = await withAuthHeaders(config);
    const reqMeta: HttpRequestMeta = {
      method: "PUT",
      url: client.defaults.baseURL
        ? normalizeUrl(client.defaults.baseURL, endpoint)
        : endpoint,
      headers: cfg.headers,
      body: data,
    };
    try {
      const res = await client.put(endpoint, data, cfg);
      return res.data as T;
    } catch (e) {
      // Ignore canceled requests - these are expected when aborting
      const isCanceledError =
        e instanceof Error &&
        (e.message === "canceled" ||
          e.name === "CanceledError" ||
          (e as any).code === "ERR_CANCELED");

      if (!isCanceledError) {
        console.error(`[authHttp] PUT ${reqMeta.url} ❌ Error:`, e);
      }

      throw toHttpError(e, reqMeta);
    }
  },
  async delete<T = any>(
    endpoint: string,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const client = buildInstance();
    const cfg = await withAuthHeaders(config);
    const reqMeta: HttpRequestMeta = {
      method: "DELETE",
      url: client.defaults.baseURL
        ? normalizeUrl(client.defaults.baseURL, endpoint)
        : endpoint,
      headers: cfg.headers,
    };
    try {
      const res = await client.delete(endpoint, cfg);
      return res.data as T;
    } catch (e) {
      // Ignore canceled requests - these are expected when aborting
      const isCanceledError =
        e instanceof Error &&
        (e.message === "canceled" ||
          e.name === "CanceledError" ||
          (e as any).code === "ERR_CANCELED");

      if (!isCanceledError) {
        console.error(`[authHttp] DELETE ${reqMeta.url} ❌ Error:`, e);
      }

      throw toHttpError(e, reqMeta);
    }
  },
};
